import { t } from "i18next";
import { useFormContext } from "react-hook-form";
import { ErrorBy, FormError, GlobalError } from "../../organisms/form";
import {
  Label,
  SelectDropdown,
  PrimaryButton,
  SecondaryButton,
} from "@sentara-health/optimahealth_web";
import { useAuth, useBranding} from "providers";
import { useNavigate } from "react-router";
import { UNPAID_GROUP_BALANCE, ViewEligibility_AccountNumber } from "utils/constants/Routes";
import { alphanumericSort } from "../../utils/helpers/HelperFn";


export const UnpaidGroupBalanceForm = ({
  showUnLocate,
  setOverDuePeriod
}: {
  showUnLocate: boolean;
  setOverDuePeriod: (value: string) => void;
}) => {
  const { register, getValues, trigger, formState, reset, resetField, watch } = useFormContext();
  const { user, isAccess } = useAuth();
  const navigate = useNavigate();
    const { brandConfig } = useBranding();

  const clearFormFields = () => {
    setOverDuePeriod("");
    reset({
      groupNumber: "",
      overduePeriod: "",
      accountId: ""
    });
    navigate(`/app/${UNPAID_GROUP_BALANCE}`);
  };

  const searchByValidate = () => {
    if (!getValues("accountId") && !getValues("overduePeriod")) {
      return t("unpaidGroupBalance.validation.eitherOneSelected");
    }
  };

    const selectedAccountId = watch("accountId");
    const uniqueAccounts = Array.from(
      new Map(
        user?.groups?.map((item) => [
          `${item.accountId}|${item.accountName}`,
          item,
        ])
      ).values()
    ).sort((a, b) => alphanumericSort(a, b, 'accountName'));
  
    const filteredGroups = selectedAccountId
      ? user?.groups?.filter((group) => group?.accountId === selectedAccountId?.split('-')[0])?.sort((a, b) => {
        const aDisplay = `${a.groupID} ${a.groupName}`;
        const bDisplay = `${b.groupID} ${b.groupName}`;
        return alphanumericSort({ combined: aDisplay }, { combined: bDisplay }, 'combined');
      })
      : user?.groups?.sort((a, b) => {
        const aDisplay = `${a.groupID} ${a.groupName}`;
        const bDisplay = `${b.groupID} ${b.groupName}`;
        return alphanumericSort({ combined: aDisplay }, { combined: bDisplay }, 'combined');
      });

    const handleAccountChange = (e: any) => {
      resetField("groupNumber");
    };

  register("searchInput", { validate: searchByValidate });
  return (
    <>
      <ErrorBy
        isShow={showUnLocate && formState.isSubmitted}
        message={t("unpaidErrorMsg")}
      />
      <div className="row">
        <div className="col-lg-7 col-md-8 col-sm-12 col-12">
          <GlobalError name="searchInput" />

          {isAccess(ViewEligibility_AccountNumber) &&
            // userLob !== USERTYPE.MEDICARE &&
             (
              <div className="mb-4">
                <label
                  className="pb-1"
                  htmlFor="selectAccount"
                  data-testid="lblAccountNumber"
                >
                  {t("orderMemberIDCard.selectAccount")}
                </label>
                <div>
                  <SelectDropdown
                    {...register("accountId", {
                      onChange: handleAccountChange,
                    })}
                    className={
                      formState.errors.accountId?.message
                        ? "form-select error-input-bdr"
                        : "form-select"
                    }
                    dataTestId="optOrderMemberIdCardAccountList"
                    id="order_id_account_id"
                    aria-describedby="order_id_account_number_error"
                  >
                    <option value="">
                      {t("orderMemberIDCard.allaccounts")}
                    </option>
                    {uniqueAccounts.map((account) => (
                      <option
                        key={`${account.accountId}-${account.accountName}`}
                        value={`${account.accountId}-${account.accountName}`}
                      >
                       {account.accountName}
                      </option>
                    ))}
                  </SelectDropdown>
                  <div
                    className={
                      formState.errors.accountId?.message ? "" : "d-none"
                    }
                  >
                    <div
                      id="order_id_account_number_error"
                      aria-live="polite"
                      aria-atomic="true"
                    >
                      <FormError name="accountId" />
                    </div>
                  </div>
                </div>
              </div>
            )}

          <div className="mb-4">
            <Label
              className="pb-1"
              dataTestId="lblGroupNumber"
              htmlFor="unpaidGroupNumber"
            >
              { t("viewEligibilityForm.selectGroupOptional")}
            </Label>
            <SelectDropdown
              data-testid="optGroupNumber"
              {...register("groupNumber", {
                onChange: () => {
                  trigger("searchInput");
                },
              })}
              className={
                formState.errors.dropdownOption?.message
                  ? " form-select error-input-bdr"
                  : "form-select"
              }
              disabled={!selectedAccountId}
              id="unpaidGroupNumber"
            >
                <option value="">{t("orderMemberIDCard.allgroups")}</option>
                  {selectedAccountId &&
                    filteredGroups?.map((item) => {
                      return (
                        <option key={`${item.groupID}`} value={item.groupID}>
                          {item.groupID} {item.groupName}
                        </option>
                      );
                    })}
            </SelectDropdown>
            <p
              className="granite-gray-color mt-2"
              data-testid="txtGroupNumberDescription"
            >
              <small>{t("unpaidGroupBalance.label.numberSpecificGroup")}</small>
            </p>
            <FormError name="groupNumber" />
          </div>

          <div className="py-2">
            <div className="pos-rel">
              <hr className="m-0" />
              <strong className="or-separator">OR</strong>
            </div>
          </div>
          <div className="mb-4">
            <Label
              className="pb-1"
              dataTestId="lblAllGroupsByOverdueAccountDays"
              htmlFor="overduePeriodDays"
            >
              {t("unpaidGroupBalance.label.overduePeriod")}
            </Label>
            <SelectDropdown
              className="form-select"
              dataTestId="optOverDueDebitPeriod"
              id="overduePeriodDays"
              {...register("overduePeriod", {
                onChange: (e) => {
                  setOverDuePeriod(e.target.value);
                  trigger("searchInput");
                },
              })}
            >
              <option value="">
                {t("unpaidGroupBalance.placeholder.overduePeriod")}
              </option>
              {[15, 30, 60, 90].map((number) => {
                return (
                  <option key={number} value={number}>
                    {t("unpaidGroupBalance.label.numberofdays", {
                      number: number,
                    })}
                  </option>
                );
              })}
            </SelectDropdown>
            <FormError name="overduePeriod" />
          </div>
        </div>
        <hr className="mt-0 mb-4" />
        <div className="d-flex flex-column flex-lg-row flex-md-row gap-3 mb-5">
          <PrimaryButton
            data-testid="btnSearch"
            id="primary"
            disabled={!(getValues("accountId").length > 0) && !(getValues("overduePeriod").length > 0)}
            type="submit"
            className={
              ((getValues("accountId") || getValues("overduePeriod"))) ?
               "primary_btn btn_block_mob"
                : "primary_btn disabled_btn btn_block_mob"
            }
          >
            {t("button.label.search")}
          </PrimaryButton>
          <SecondaryButton
            data-testid="btnClear"
            id="clear_btn"
            type="button"
            onClick={clearFormFields}
            className="secondary_btn"
          >
            {t("button.label.clear")}
          </SecondaryButton>
        </div>
      </div>
    </>
  );
};

export default UnpaidGroupBalanceForm;
